/**
 * CustomFieldsService
 *
 * This service handles all custom field-related operations in the application.
 * It provides methods for managing custom fields, group rules, and custom values
 * through the API. Custom fields allow for dynamic data structures and additional
 * metadata for various entities in the system.
 *
 * @class
 */
import {
  pathAssocCustomFieldsByMatter,
  pathCustomFieldId,
  pathCustomFields,
  pathCustomValueHolding,
  pathCustomValueHoldingId,
  pathGroupRuleId,
  pathGroupRules,
} from '@frontend/__settings__/septSoftwarePaths';
import HttpService from './HttpService';
import type {
  CreateCustomFieldRequest,
  CreateCustomFieldResponse,
  GetCustomFieldsResponse,
} from '@frontend/Models/CustomField/CustomFieldRequestResponseModels';
import type {
  CreateGroupRuleRequest,
  CreateGroupRuleResponse,
  GetGroupRulesResponse,
} from '@frontend/Models/GroupRule/GroupRuleRequestResponseModels';
import type {
  CreateCustomValueRequest,
  CreateCustomValueResponse,
  GetCustomValuesResponse,
} from '@frontend/Models/CustomValue/CustomValueRequestResponseModels';

const http = new HttpService();

class CustomFieldsService {
  /**
   * Retrieves all custom fields from the system
   *
   * @returns {Promise<Object>} Response containing custom fields data
   * @throws {Error} If the API request fails
   */
  static async getCustomFields(): Promise<GetCustomFieldsResponse> {
    const data = await http.get<GetCustomFieldsResponse>(
      `/${pathCustomFields}`
    );
    return data;
  }

  /**
   * Creates a new custom field in the system
   *
   * @param {Object} customFieldData - The custom field data to be created
   * @param {string} customFieldData.name - Name of the custom field
   * @param {string} customFieldData.type - Type of the custom field
   * @param {string} [customFieldData.description] - Description of the custom field
   *
   * @returns {Promise<Object>} Response containing the created custom field
   * @throws {Error} If the API request fails
   */
  static async createCustomField(
    customFieldData: CreateCustomFieldRequest
  ): Promise<CreateCustomFieldResponse> {
    const response = await http.post<CreateCustomFieldResponse>(
      `/${pathCustomFields}`,
      customFieldData
    );
    return response;
  }

  /**
   * Updates an existing custom field
   *
   * @param {string} customFieldId - ID of the custom field to update
   * @param {Object} customFieldData - Updated custom field data
   *
   * @returns {Promise<Object>} Response containing the updated custom field
   * @throws {Error} If the API request fails
   */
  static async updateCustomField(
    customFieldId: string,
    customFieldData: Partial<CreateCustomFieldRequest>
  ): Promise<CreateCustomFieldResponse> {
    const response = await http.put<CreateCustomFieldResponse>(
      `/${pathCustomFieldId(customFieldId)}`,
      customFieldData
    );
    return response;
  }

  /**
   * Deletes a custom field from the system
   *
   * @param {string} customFieldId - ID of the custom field to delete
   * @returns {Promise<Object>} Response indicating success or failure
   * @throws {Error} If the API request fails
   */
  static async deleteCustomField(
    customFieldId: string
  ): Promise<CreateCustomFieldResponse> {
    const response = await http.delete<CreateCustomFieldResponse>(
      `/${pathCustomFieldId(customFieldId)}`
    );
    return response;
  }

  /**
   * Retrieves custom fields associated with a specific matter
   *
   * @param {string} matterId - ID of the matter
   * @returns {Promise<Object>} Response containing matter-specific custom fields
   * @throws {Error} If the API request fails
   */
  static async getMatterCustomFields(
    matterId: string
  ): Promise<GetCustomFieldsResponse> {
    const response = await http.get<GetCustomFieldsResponse>(
      `/${pathAssocCustomFieldsByMatter(matterId)}`
    );
    return response;
  }

  /**
   * Retrieves all group rules from the system
   *
   * @returns {Promise<Object>} Response containing group rules data
   * @throws {Error} If the API request fails
   */
  static async getGroupRules(): Promise<GetGroupRulesResponse> {
    const response = await http.get<GetGroupRulesResponse>(pathGroupRules);
    return response;
  }

  /**
   * Creates a new group rule
   *
   * @param {Object} groupRuleData - The group rule data to be created
   * @returns {Promise<Object>} Response containing the created group rule
   * @throws {Error} If the API request fails
   */
  static async createGroupRule(
    groupRuleData: CreateGroupRuleRequest
  ): Promise<CreateGroupRuleResponse> {
    const response = await http.post<CreateGroupRuleResponse>(
      pathGroupRules,
      groupRuleData
    );
    return response;
  }

  /**
   * Updates an existing group rule
   *
   * @param {string} ruleId - ID of the group rule to update
   * @param {Object} groupRuleData - Updated group rule data
   * @returns {Promise<Object>} Response containing the updated group rule
   * @throws {Error} If the API request fails
   */
  static async updateGroupRule(
    ruleId: string,
    groupRuleData: CreateGroupRuleRequest
  ): Promise<CreateGroupRuleResponse> {
    const response = await http.put<CreateGroupRuleResponse>(
      pathGroupRuleId(ruleId),
      groupRuleData
    );
    return response;
  }

  /**
   * Deletes a group rule from the system
   *
   * @param {string} ruleId - ID of the group rule to delete
   * @returns {Promise<Object>} Response indicating success or failure
   * @throws {Error} If the API request fails
   */
  static async deleteGroupRule(
    ruleId: string
  ): Promise<CreateGroupRuleResponse> {
    const response = await http.delete<CreateGroupRuleResponse>(
      pathGroupRuleId(ruleId)
    );
    return response;
  }

  /**
   * Retrieves all custom values from the system
   *
   * @returns {Promise<Object>} Response containing custom values data
   * @throws {Error} If the API request fails
   */
  static async getCustomValues(): Promise<GetCustomValuesResponse> {
    const response = await http.get<GetCustomValuesResponse>(
      pathCustomValueHolding
    );
    return response;
  }

  /**
   * Creates a new custom value
   *
   * @param {Object} customValueData - The custom value data to be created
   * @returns {Promise<Object>} Response containing the created custom value
   * @throws {Error} If the API request fails
   */
  static async createCustomValue(
    customValueData: CreateCustomValueRequest
  ): Promise<CreateCustomValueResponse> {
    const response = await http.post<CreateCustomValueResponse>(
      pathCustomValueHolding,
      customValueData
    );
    return response;
  }
  /**
   * Updates an existing custom value
   *
   * @param {string} vhId - ID of the custom value to update
   * @param {Object} customValueData - Updated custom value data
   * @returns {Promise<Object>} Response containing the updated custom value
   * @throws {Error} If the API request fails
   */
  static async updateCustomValue(
    vhID: string,
    customValueData: Partial<CreateCustomValueRequest>
  ): Promise<CreateCustomValueResponse> {
    const response = await http.put<CreateCustomValueResponse>(
      pathCustomValueHoldingId(vhID),
      customValueData
    );
    return response;
  }

  // TODO: This is matter the paths above are vhId
  /**
   * Retrieves custom values associated with a specific matter
   *
   * @param {string} matterID - ID of the matter
   * @returns {Promise<Object>} Response containing matter-specific custom values
   * @throws {Error} If the API request fails
   */
  static async getMatterCustomValues(
    matterID: string
  ): Promise<GetCustomValuesResponse> {
    const response = await http.get<GetCustomValuesResponse>(
      pathCustomValueHoldingId(matterID)
    );
    return response;
  }
}

export default CustomFieldsService;
